import * as ss from 'superstruct';
import { AdAccountPlatform } from './ad-account-platform';

export const AdDataStreamSourceList = [
  'google-ads',
  'microsoft-ads',
  'meta-ads',
  'openai-ads',
] as const;

export type AdDataStreamSource = (typeof AdDataStreamSourceList)[number];

/**
 * An ad data stream source's configuration: its name plus flat, source-specific
 * options. Credentials are platform-wide or on the ad account, so there are
 * no secrets here.
 */
export const AdDataStreamSourceConfig = ss.intersection([
  ss.type({ name: ss.string() }),
  ss.record(
    ss.string(),
    ss.optional(
      ss.nullable(ss.union([ss.string(), ss.number(), ss.boolean()])),
    ),
  ),
]);
export type AdDataStreamSourceConfig = ss.Infer<
  typeof AdDataStreamSourceConfig
>;

export const GoogleAdsDataStreamSource = ss.type({
  name: ss.literal('google-ads'),
});

export const MicrosoftAdsDataStreamSource = ss.type({
  name: ss.literal('microsoft-ads'),
});

/**
 * Meta reports conversions as `actions` keyed by action type, and which ones
 * count depends on the account's objective. These are Meta's aggregate types:
 * `lead` already includes pixel (`offsite_conversion.fb_pixel_lead`) and
 * on-Facebook (`onsite_conversion.lead_grouped`) leads, so listing an
 * aggregate with its components counts the same conversion twice.
 */
export const DefaultMetaConversionActionTypes = ['lead', 'purchase'];

export const MetaAdsDataStreamSource = ss.type({
  name: ss.literal('meta-ads'),
  /**
   * Comma-separated Meta action types counted as platform conversions.
   * Defaults to `DefaultMetaConversionActionTypes`. Don't mix an aggregate
   * type with its components.
   */
  conversionActionTypes: ss.optional(ss.nullable(ss.string())),
});

export const OpenAiAdsDataStreamSource = ss.type({
  name: ss.literal('openai-ads'),
  /**
   * Count 1-day view-through conversions as well as 30-day click-through
   * ones. Off by default, matching the Conversions column in OpenAI's Ads
   * Manager.
   */
  includeViewThroughConversions: ss.optional(ss.nullable(ss.boolean())),
});

/** Each source's own config struct, for validating writes. */
export const AdDataStreamSourceStructs: Record<
  AdDataStreamSource,
  ss.Struct<any, any>
> = {
  'google-ads': GoogleAdsDataStreamSource,
  'microsoft-ads': MicrosoftAdsDataStreamSource,
  'meta-ads': MetaAdsDataStreamSource,
  'openai-ads': OpenAiAdsDataStreamSource,
};

export const adDataStreamSourceConfig: Record<
  AdDataStreamSource,
  { platform: AdAccountPlatform; label: string }
> = {
  'google-ads': { platform: 'google', label: 'Google Ads' },
  'microsoft-ads': { platform: 'bing', label: 'Microsoft Ads' },
  'meta-ads': { platform: 'meta', label: 'Meta Ads' },
  'openai-ads': { platform: 'openai', label: 'OpenAI Ads' },
};

export const isAdDataStreamSource = (
  name: string,
): name is AdDataStreamSource =>
  (AdDataStreamSourceList as readonly string[]).includes(name);

/** The source that pulls an ad account's data on `platform`. */
export const adDataStreamSourceForPlatform = (
  platform: AdAccountPlatform,
): AdDataStreamSource => {
  const source = AdDataStreamSourceList.find(
    (s) => adDataStreamSourceConfig[s].platform === platform,
  );
  if (!source) {
    throw new Error(`No ad data stream source for platform ${platform}`);
  }
  return source;
};

/** Meta conversion action types from a stream's comma-separated option. */
export const parseMetaConversionActionTypes = (
  raw: string | null | undefined,
): string[] => {
  const types = (raw ?? '')
    .split(',')
    .map((t) => t.trim())
    .filter((t) => t.length > 0);
  return types.length > 0 ? types : DefaultMetaConversionActionTypes;
};
